import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { StaffingPeriodsEditor } from './StaffingPeriodsEditor.jsx';
import { StationsEditor } from './StationsEditor.jsx';

// ─── Location Config (Round 17) — operating hours, blackouts, staffing ──────
export function LocationConfigView({ locations, currentUser, location }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  // R45: scope to the location selected in the top menu. The inline
  // tab strip across all locations is gone — switch via the dropdown
  // up top instead. activeId follows the prop.
  const activeId = location?.id || locations[0]?.id || null;
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingMsg, setSavingMsg] = useState('');
  const [floatInput, setFloatInput] = useState('');
  const dows = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const dowLabels = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

  async function load() {
    if (!activeId) return;
    setLoading(true);
    try {
      const r = await api('GET', `/api/locations/${activeId}/config`, null, headers);
      // Hydrate operating hours into a 7-day array even if some days
      // aren't in the DB yet.
      const byDow = new Map();
      (r.operating_hours || []).filter((h) => !h.season).forEach((h) => byDow.set(h.day_of_week, h));
      r._hours = dows.map((d) => byDow.get(d) || { day_of_week: d, open_time: '05:30', close_time: '19:00', closed: 0 });
      // Round 36: legacy per-DoW minimum-staff numbers replaced by named
      // staffing periods (Open/Peak/Post-Peak/Close), edited via
      // StaffingPeriodsEditor below — no _targets hydration needed.
      setConfig(r);
    } catch (e) { setSavingMsg(`Load failed: ${e.message || e}`); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [activeId]);

  async function saveHours() {
    setSavingMsg('Saving…');
    try {
      await api('PUT', `/api/locations/${activeId}/operating-hours`,
        { items: config._hours.map((h) => ({
            day_of_week: h.day_of_week,
            open_time: h.open_time,
            close_time: h.close_time,
            closed: !!h.closed,
          })) }, headers);
      setSavingMsg('Operating hours saved.');
      load();
    } catch (e) { setSavingMsg(`Save failed: ${e.message || e}`); }
  }

  async function addBlackout(label, start, end, note) {
    if (!label || !start || !end) return;
    try {
      await api('POST', `/api/locations/${activeId}/blackouts`,
        { label, start_date: start, end_date: end, note }, headers);
      load();
    } catch (e) { setSavingMsg(`Blackout failed: ${e.message || e}`); }
  }

  async function removeBlackout(bid) {
    if (!confirm('Remove this blackout window?')) return;
    try {
      await api('DELETE', `/api/locations/${activeId}/blackouts/${bid}`, null, headers);
      load();
    } catch (e) { setSavingMsg(`Delete failed: ${e.message || e}`); }
  }

  // Opening float — the reminder for what to leave in the drawer at close.
  // Set once per location by a manager. Never subtracted from the daily
  // count; the drop-in-safe number IS the counted amount.
  const currentFloat = config?.location?.opening_float_dollars;
  useEffect(() => {
    setFloatInput(currentFloat == null ? '' : String(currentFloat));
  }, [currentFloat, activeId]);
  async function saveFloat() {
    setSavingMsg('Saving opening float…');
    try {
      const raw = floatInput.trim();
      const val = raw === '' ? null : Number(raw);
      if (val != null && (!Number.isFinite(val) || val < 0)) throw new Error('Enter a non-negative dollar amount, or leave blank.');
      await api('PUT', `/api/locations/${activeId}/opening-float`, { dollars: val }, headers);
      setSavingMsg('Opening float saved.');
      load();
    } catch (e) { setSavingMsg(`Save failed: ${e.message || e}`); }
  }

  return (
    <div className="space-y-3">
      <div className="card">
        <div className="text-base font-semibold">{location?.name || 'Location'} settings</div>
        <div className="text-xs text-gray-500">
          Operating hours, blackout windows, and minimum staffing targets for {location?.name || 'this location'}.
          Switch locations using the dropdown in the top menu.
        </div>
      </div>

      {savingMsg && <div className="text-xs" style={{ color: '#37474f' }}>{savingMsg}</div>}
      {loading && <div className="card text-sm text-gray-500">Loading…</div>}

      {config && (
        <>
          {/* Opening float — reminder for the closing barista, per location. */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div className="text-sm font-semibold">Opening float</div>
                <div className="text-xs text-gray-500" style={{ marginTop: 2 }}>
                  Dollars the closing barista leaves in the drawer for the next open. Not subtracted from any count.
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, color: '#757575' }}>$</span>
                <input type="number" step="1" min="0"
                  value={floatInput}
                  onChange={(e) => setFloatInput(e.target.value)}
                  disabled={!config.canEdit}
                  placeholder="not set"
                  style={{ width: 100, padding: '5px 8px', fontSize: 13, textAlign: 'right', border: '1px solid #d0d4d9', borderRadius: 4, background: '#fff' }} />
                {config.canEdit && (
                  <button onClick={saveFloat}
                    style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Operating hours */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div className="text-sm font-semibold">Operating hours</div>
              {config.canEdit && (
                <button onClick={saveHours}
                  style={{ background: 'var(--forest)', color: '#fff', border: 'none',
                            borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Save hours
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
              {config._hours.map((h, i) => (
                <div key={h.day_of_week} style={{ padding: 8, border: '1px solid #e0e0e0', borderRadius: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{dowLabels[h.day_of_week]}</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#757575', marginBottom: 4 }}>
                    <input type="checkbox" disabled={!config.canEdit} checked={!!h.closed}
                      onChange={(e) => setConfig((c) => {
                        const next = [...c._hours]; next[i] = { ...next[i], closed: e.target.checked ? 1 : 0 };
                        return { ...c, _hours: next };
                      })} /> Closed
                  </label>
                  {!h.closed && (
                    <>
                      <input type="time" step="1800" disabled={!config.canEdit}
                        value={h.open_time || '05:30'}
                        onChange={(e) => setConfig((c) => {
                          const next = [...c._hours]; next[i] = { ...next[i], open_time: e.target.value };
                          return { ...c, _hours: next };
                        })}
                        style={{ width: '100%', padding: '3px 6px', fontSize: 11, border: '1px solid #d0d4d9', borderRadius: 4, marginBottom: 3 }} />
                      <input type="time" step="1800" disabled={!config.canEdit}
                        value={h.close_time || '19:00'}
                        onChange={(e) => setConfig((c) => {
                          const next = [...c._hours]; next[i] = { ...next[i], close_time: e.target.value };
                          return { ...c, _hours: next };
                        })}
                        style={{ width: '100%', padding: '3px 6px', fontSize: 11, border: '1px solid #d0d4d9', borderRadius: 4 }} />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Stations — manager-configurable list per location. Used by the
              shift dashboard to assign people to bar/register/pastry/etc. */}
          <StationsEditor
            locationId={activeId}
            currentUser={currentUser}
            canEdit={config.canEdit}
          />

          {/* Staffing periods (Open/Peak/Post-Peak/Close) — replaces the
              legacy minimum-staff-per-day block. Periods carry headcount,
              days_of_week applicability, and effective date ranges. */}
          <StaffingPeriodsEditor
            locationId={activeId}
            currentUser={currentUser}
            canEdit={config.canEdit}
          />

          {/* Blackout windows used to live here. CJ feedback 2026-05-11:
              redundant with restricted-staffing events. Use Settings →
              Events with the "Restricted staffing date" toggle instead. */}
        </>
      )}
    </div>
  );
}
